import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, TextField, Button,
  MenuItem
} from '@mui/material';

const API_BASE = process.env.REACT_APP_API_BASE || '';

const blank = {
  ServiceId: null,
  ServiceDate: '',
  Description: '',
  Odometer: '',
  TotalCost: '',
  ProviderId: '',
  Notes: ''
};

/**
 * Normalizes an initial service record.
 * Handles missing values, ID fallbacks, and converts dates and numeric fields to string inputs.
 * @param {Object | null | undefined} initial - Raw record from history grid or API.
 * @returns {Object} Form-ready service record.
 */
function toForm(initial) {
  if (!initial) return blank;
  return {
    ServiceId: initial.ServiceId ?? initial.id ?? null,
    ServiceDate: (initial.ServiceDate || '').slice(0, 10), // 'YYYY-MM-DD'
    Description: initial.Description ?? '',
    Odometer: String(initial.Odometer ?? initial.Mileage ?? ''), // Mileage -> Odometer
    TotalCost: String(initial.TotalCost ?? ''),
    ProviderId: initial.ProviderId != null ? String(initial.ProviderId) : '', // Provider -> ProviderName
    Notes: initial.Notes ?? ''
  };
}

/**
 * ServiceDialog allows users to create or edit a single maintenance/service record for a vehicle.
 * It loads providers for the dropdown, normalizes incoming data for editing, and returns a typed payload
 * through "onSave".
 * 
 * @param {Object} props 
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {Object | null} props.initial - Existing service record to edit, or null for a new record.
 * @param {(payload: Object) => void} props.onSave - Called with the prepared service payload when saving.
 * @param {() => void} props.onClose - Called when the dialog should be closed without saving. 
 * @returns {JSX.Element}
 */
export default function ServiceDialog({ open, initial, onSave, onClose }) {
  const [f, setF] = useState(blank);
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  useEffect(() => {
    setF(toForm(initial));
  }, [initial]);

  useEffect(() => {
    if (!open) return;

    (async () => {
      try {
        setLoadingProviders(true);

        // load provider list for the menu
        const res = await axios.get(`${API_BASE}/api/providers`);
        const providerList = res.data?.rows ?? [];
        setProviders(providerList);

        // Prefer an ID if the row already has it
        let pid = initial?.ProviderId;

        // If no ID, try to resolve by name against the list we just loaded
        if (pid == null && initial?.Provider) {
          const normalize = (s) => (s || '').trim().toLowerCase();
          const match = providerList.find(
            (p) => normalize(p.ProviderName) === normalize(initial.Provider)
          );
          if (match) {
            pid = match.ProviderId;
          }
        }

        if (pid != null) {
          setF(prev => ({ ...prev, ProviderId: String(pid) }));
        }
      } catch (e) {
        console.error('failed to load providers', e);
      } finally {
        setLoadingProviders(false);
      }
    })();
  }, [open, initial]);

  /**
   * Change handler for form fields that updates the local service state.
   * 
   * @param {React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>} e 
   */
  const update = (e) => setF((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  /**
   * Prepares the service payload with proper types (e.g., numeric ProviderId) 
   * and delegates to "onSave".
   * 
   * Closes the dialog immediately after invoking the save callback.
   */
  const save = () => {
    const payload = {
      ...f,
      ProviderId: f.ProviderId ? Number(f.ProviderId) : null
    };
    onSave(payload); 
    onClose(); 
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{initial ? 'Edit Service Record' : 'Add Service Record'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: .5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              name="ServiceDate"
              label="Date (YYYY-MM-DD)"
              type="date"
              value={f.ServiceDate}
              onChange={update}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField name="Description" label="Description" value={f.Description} onChange={update} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField name="Odometer" label="Odometer (km)" type="number" value={f.Odometer} onChange={update} fullWidth />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField name="TotalCost" label="Total Cost" type="number" value={f.TotalCost} onChange={update} fullWidth />
          </Grid>
          <Grid item xs={12}>
            <TextField 
              select
              name="ProviderId" 
              label="Provider" 
              value={f.ProviderId ?? ''} 
              onChange={update} 
              fullWidth 
              helperText={loadingProviders ? 'Loading Providers...' : (providers.length ? '' : 'No providers found' )}
            >
              {providers.map(p => (
                <MenuItem key={p.ProviderId} value={String(p.ProviderId)}>
                  {p.ProviderName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField name="Notes" label="Notes" value={f.Notes} onChange={update} fullWidth multiline minRows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}
